import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import RTFilters from '../common/RTFilters';
import { apiGet } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import { OCCUPANCY_GENDER_FILTER_OPTIONS } from '../../utils/occupancyConstants';
import { formatDateTimeToDDMMYYYY } from '../../utils/dateHelper';
import '../common/rtCommon.css';
import RTPageLayout from '../common/RTPageLayout';
import RTStatusPill from '../common/statusPill';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'paid', label: 'Paid' },
  { value: 'unpaid', label: 'Unpaid' },
];

const getDefaultFilters = () => ({
  month: new Date().getMonth() + 1,
  year: new Date().getFullYear(),
  status: '',
  occupancy_gender: '',
});

const RentCollectionReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 2; year <= currentYear + 1; year++) {
      years.push({ value: year, label: year.toString() });
    }
    return years;
  }, []);

  const filterFields = useMemo(
    () => [
      {
        key: 'month',
        label: 'Month',
        type: 'dropdown',
        width: 'compact',
        options: MONTH_OPTIONS,
      },
      {
        key: 'year',
        label: 'Year',
        type: 'dropdown',
        width: 'compact',
        options: yearOptions,
      },
      {
        key: 'status',
        label: 'Status',
        type: 'dropdown',
        width: 'compact',
        options: STATUS_OPTIONS,
      },
      {
        key: 'occupancy_gender',
        label: 'Gender',
        type: 'dropdown',
        width: 'compact',
        options: OCCUPANCY_GENDER_FILTER_OPTIONS,
        applyImmediately: true,
      },
    ],
    [yearOptions],
  );

  useEffect(() => {
    loadReport(appliedFilters);
  }, []);

  const loadReport = async (filterParams = appliedFilters) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        month: String(filterParams.month),
        year: String(filterParams.year),
      });
      if (filterParams.occupancy_gender) {
        params.append('occupancy_gender', filterParams.occupancy_gender);
      }
      const response = await apiGet(
        `/reports/rent-collection/?${params.toString()}`,
      );

      if (response.data && response.data.success) {
        setInvoices(response.data.data || []);
        setSummary(response.data.summary || null);
        toastSuccess(`Loaded ${(response.data.data || []).length} records`);
      } else {
        toastError('Failed to load report');
      }
    } catch (error) {
      console.error('Error loading rent collection report:', error);
      toastError('Error loading report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value, { applyImmediately } = {}) => {
    const next = { ...filters, [key]: value ?? '' };
    setFilters(next);
    if (applyImmediately) {
      setAppliedFilters(next);
      if (key === 'month' || key === 'year' || key === 'occupancy_gender') {
        loadReport(next);
      }
    }
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    loadReport(filters);
  };

  const handleClearFilters = () => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    loadReport(defaultFilters);
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    const monthLabel = MONTH_OPTIONS.find(
      (o) => o.value === appliedFilters.month,
    )?.label;
    if (monthLabel) {
      pills.push({ key: 'month', label: `Month: ${monthLabel}` });
    }
    if (appliedFilters.year) {
      pills.push({ key: 'year', label: `Year: ${appliedFilters.year}` });
    }
    if (appliedFilters.status) {
      const statusLabel =
        STATUS_OPTIONS.find((o) => o.value === appliedFilters.status)?.label ||
        appliedFilters.status;
      pills.push({ key: 'status', label: `Status: ${statusLabel}` });
    }
    if (appliedFilters.occupancy_gender) {
      const genderLabel =
        OCCUPANCY_GENDER_FILTER_OPTIONS.find(
          (o) => o.value === appliedFilters.occupancy_gender,
        )?.label || appliedFilters.occupancy_gender;
      pills.push({ key: 'occupancy_gender', label: `Gender: ${genderLabel}` });
    }
    return pills;
  }, [appliedFilters]);

  const handleRemoveAppliedFilter = (key) => {
    const next = { ...appliedFilters };
    if (key === 'month') {
      next.month = new Date().getMonth() + 1;
    } else if (key === 'year') {
      next.year = new Date().getFullYear();
    } else if (key === 'occupancy_gender') {
      next.occupancy_gender = '';
    } else {
      next[key] = '';
    }
    setFilters(next);
    setAppliedFilters(next);
    if (key === 'month' || key === 'year' || key === 'occupancy_gender') {
      loadReport(next);
    }
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handlePGClick = (pgId) => {
    window.open(`/paying-guest/view/${pgId}`, '_blank');
  };

  const handleBuildingClick = (buildingId) => {
    window.open(`/building/view/${buildingId}`, '_blank');
  };

  const getStatusBadge = (status) => (
    <RTStatusPill status={status || 'draft'} />
  );

  const filteredInvoices = appliedFilters.status
    ? invoices.filter((inv) => {
        if (appliedFilters.status === 'paid') return inv.status === 'paid';
        if (appliedFilters.status === 'unpaid') return inv.status !== 'paid';
        return true;
      })
    : invoices;

  const tableData = useMemo(
    () =>
      filteredInvoices.map((inv) => ({
        ...inv,
        room_group_key:
          inv.room_id != null
            ? `${inv.building_id}-${inv.room_id}`
            : `invoice-${inv.invoice_id}`,
      })),
    [filteredInvoices],
  );

  const columns = [
    {
      Header: 'Building',
      accessor: 'building_name',
      Cell: ({ value, row }) =>
        row.building_id ? (
          <RTButton
            type="link"
            onClick={() => handleBuildingClick(row.building_id)}
            className="rt-link-button"
          >
            {value || 'N/A'}
          </RTButton>
        ) : (
          value || 'N/A'
        ),
      exportValue: (row) => row.building_name || 'N/A',
    },
    {
      Header: 'Room',
      accessor: 'room_name',
      Cell: ({ value }) => value || 'N/A',
      exportValue: (row) => row.room_name || 'N/A',
    },
    {
      Header: 'Invoice #',
      accessor: 'invoice_number',
      Cell: ({ value }) => value || 'N/A',
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value, row }) =>
        row.pg_id ? (
          <RTButton
            type="link"
            onClick={() => handlePGClick(row.pg_id)}
            className="rt-link-button"
          >
            {value || 'N/A'}
          </RTButton>
        ) : (
          value || 'N/A'
        ),
      exportValue: (row) => row.pg_name || 'N/A',
    },
    {
      Header: 'Mobile',
      accessor: 'mobile',
      Cell: ({ value }) => value || 'N/A',
    },
    {
      Header: 'Invoice Items',
      accessor: 'items_text',
      Cell: ({ value }) => value || 'N/A',
    },
    {
      Header: 'Amount',
      accessor: 'total_amount',
      Cell: ({ value }) => formatAmount(value),
      exportValue: (row) => row.total_amount || 0,
    },
    {
      Header: 'Paid',
      accessor: 'amount_paid',
      Cell: ({ value }) => formatAmount(value),
      exportValue: (row) => row.amount_paid || 0,
    },
    {
      Header: 'Paid On',
      accessor: 'paid_on',
      Cell: ({ value }) => (value ? formatDateTimeToDDMMYYYY(value) : 'N/A'),
      exportValue: (row) =>
        row.paid_on ? formatDateTimeToDDMMYYYY(row.paid_on) : 'N/A',
    },
    {
      Header: 'Collected By',
      accessor: 'collected_by',
      Cell: ({ value, row }) => {
        if (!value) return 'N/A';
        if (!row.payment_id) return value;
        return (
          <RTButton
            type="link"
            onClick={() =>
              window.open(`/payments/${row.payment_id}/view`, '_blank')
            }
            className="rt-link-button"
          >
            {value}
          </RTButton>
        );
      },
      exportValue: (row) => row.collected_by || 'N/A',
    },
    {
      Header: 'Status',
      accessor: 'status',
      align: 'center',
      Cell: ({ value }) => getStatusBadge(value),
      exportValue: (row) => row.status || '',
    },
  ];

  return (
    <RTPageLayout activePath={location.pathname} title="Rent Collection Report">
      <div className="rt-section" style={{ flex: 'none' }}>
        <RTFilters
          fields={filterFields}
          values={filters}
          onChange={handleFilterChange}
          showApplyButton={true}
          onApply={handleApplyFilters}
          pills={appliedFilterPills}
          onRemovePill={handleRemoveAppliedFilter}
          onClearAll={handleClearFilters}
        />
      </div>

      {summary && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem',
            marginBottom: '0.75rem',
          }}
        >
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #153C7F',
              borderRight: '4px solid #153C7F',
              borderRadius: '8px',
              padding: '0.4rem 0.8rem',
            }}
          >
            <div
              style={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Total Invoices
            </div>
            <div
              style={{ fontSize: '1.1rem', fontWeight: 700, color: '#153C7F' }}
            >
              {summary.total_invoices}
            </div>
          </div>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #153C7F',
              borderRight: '4px solid #153C7F',
              borderRadius: '8px',
              padding: '0.4rem 0.8rem',
            }}
          >
            <div
              style={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Paid
            </div>
            <div
              style={{ fontSize: '1.1rem', fontWeight: 700, color: '#153C7F' }}
            >
              {summary.paid_count}
            </div>
          </div>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #153C7F',
              borderRight: '4px solid #153C7F',
              borderRadius: '8px',
              padding: '0.4rem 0.8rem',
            }}
          >
            <div
              style={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Unpaid
            </div>
            <div
              style={{ fontSize: '1.1rem', fontWeight: 700, color: '#153C7F' }}
            >
              {summary.unpaid_count}
            </div>
          </div>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #153C7F',
              borderRight: '4px solid #153C7F',
              borderRadius: '8px',
              padding: '0.4rem 0.8rem',
            }}
          >
            <div
              style={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Total Amount
            </div>
            <div
              style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}
            >
              {formatAmount(summary.total_amount)}
            </div>
          </div>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #153C7F',
              borderRight: '4px solid #153C7F',
              borderRadius: '8px',
              padding: '0.4rem 0.8rem',
            }}
          >
            <div
              style={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Total Paid
            </div>
            <div
              style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}
            >
              {formatAmount(summary.total_paid)}
            </div>
          </div>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #153C7F',
              borderRight: '4px solid #153C7F',
              borderRadius: '8px',
              padding: '0.4rem 0.8rem',
            }}
          >
            <div
              style={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Total Unpaid
            </div>
            <div
              style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}
            >
              {formatAmount(summary.total_unpaid)}
            </div>
          </div>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #153C7F',
              borderRight: '4px solid #153C7F',
              borderRadius: '8px',
              padding: '0.4rem 0.8rem',
            }}
          >
            <div
              style={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Adjustments
            </div>
            <div
              style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}
            >
              {formatAmount(summary.total_adjustments)}
            </div>
          </div>
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderLeft: '4px solid #153C7F',
              borderRight: '4px solid #153C7F',
              borderRadius: '8px',
              padding: '0.4rem 0.8rem',
            }}
          >
            <div
              style={{
                fontSize: '0.6rem',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Collected Rent
            </div>
            <div
              style={{ fontSize: '0.85rem', fontWeight: 700, color: '#153C7F' }}
            >
              {formatAmount(summary.collected_rent)}
            </div>
          </div>
        </div>
      )}

      <div className="rt-section">
        {loading ? (
          <div className="text-center">
            <RTSpinner message="Loading rent collection report..." />
          </div>
        ) : (
          <Table
            columns={columns}
            data={tableData}
            title="Rent Collection Report"
            exportable={true}
            showSearch={true}
            showPagination={true}
            disableSort={true}
            noDataText="No rent collection data found for the selected period"
            rowGroupConfig={{
              groups: [{ key: 'room_group_key', columns: ['room_name'] }],
            }}
          />
        )}
      </div>
    </RTPageLayout>
  );
};

export default RentCollectionReport;
